import { InfoCircleOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal } from '@gravitate-js/excalibrr'
import { formatters } from '@utils/intl/dateTimeFormatters'
import { DatePicker, type DatePickerProps, Select } from 'antd'
import type { AnyObject } from 'antd/es/_util/type'
import type { RangePickerProps } from 'antd/es/date-picker/generatePicker/interface'
import dayjs, { type Dayjs } from 'dayjs'
import { cloneDeep } from 'lodash'
import { forwardRef, useCallback, useEffect, useMemo, useState } from 'react'

const timeOptions = new Array(24).fill(0).map((_, i) => {
  return {
    value: i,
    label: `${(i < 12 ? i : i - 12) || 12}:00 ${i < 12 ? 'AM' : 'PM'}`,
  }
})
const setSimpleTime = (date: Dayjs) => date.startOf('h')

type TimeWindowInputProps<T extends AnyObject = Dayjs> = Omit<
  RangePickerProps<T>,
  'id' | 'presets' | 'defaultValue'
> & {
  defaultInputType?: 'simple' | 'custom'
  onInputTypeChanged?: (type: 'simple' | 'custom') => void
}
type PickerRef = {
  nativeElement: HTMLDivElement
  focus: (options?: FocusOptions) => void
  blur: VoidFunction
}

const rangeDisabledTimeToDisabledTime = ({ value, disabledTime }: Pick<RangePickerProps, 'value' | 'disabledTime'>) =>
  disabledTime === undefined
    ? undefined
    : (c: Dayjs) => disabledTime(c, 'start', { from: value?.[0] ?? value?.[1] ?? undefined })

const formatValueToRange = (newDate: Dayjs | null, value: RangePickerProps['value']): [Dayjs | null, Dayjs | null] => {
  if (!newDate) return [null, null]

  const [start, end] = value?.length ? value : [newDate, null]

  return [start ? newDate.hour(start.hour()) : null, end ? newDate.hour(end.hour()) : null]
}

const simpleInput: React.CSSProperties = {
  minWidth: 120,
}

const formatOnChangeResult = (
  start?: Dayjs | null,
  end?: Dayjs | null
): Parameters<Required<RangePickerProps>['onChange']> => [
  [start ?? null, end ?? null],
  [start?.toISOString() ?? '', end?.toISOString() ?? ''],
]

export const DateWindowInput = forwardRef<PickerRef, TimeWindowInputProps>(
  ({ defaultInputType, onChange, onInputTypeChanged, value, ...rest }, ref) => {
    const [isSimple, _setIsSimple] = useState(
      defaultInputType === 'simple' || !value || (value?.[1] && value?.[0]?.isSame(value[1], 'day'))
    )
    const setIsSimple = useCallback(
      (isSimple: boolean) => {
        onInputTypeChanged?.(isSimple ? 'simple' : 'custom')
        _setIsSimple(isSimple)
      },
      [onInputTypeChanged]
    )

    const disabled = Array.isArray(rest.disabled) ? rest.disabled[0] : rest.disabled
    const placeholder = Array.isArray(rest.placeholder) ? rest.placeholder[0] : rest.placeholder
    const defaultPickerValue = Array.isArray(rest.defaultPickerValue)
      ? rest.defaultPickerValue[0]
      : rest.defaultPickerValue

    const mode = Array.isArray(rest.mode) ? rest.mode[0] : rest.mode

    const disabledTime = useMemo(
      () => rangeDisabledTimeToDisabledTime({ value, disabledTime: rest.disabledTime }),
      [rest.disabledTime, value]
    )

    useEffect(() => {
      if (!value?.length || !isSimple) return

      // desired behaviors for switching from custom -> simple:
      // 1. the date should be the minimum of the two dates, if they both exist
      // 2. the end time must be greater than the start time, if they both exist
      // 3. rather than disabling/enabling values outside of the range, they should flip if both exist
      // 4. one or no dates may exist
      // 5. times must be zero'ed out

      let [start, end] = cloneDeep(value)
      //handles reqs 1 and 5
      const dateToSet = setSimpleTime(start && end ? dayjs.min(start, end) : (start ?? end ?? dayjs()))
      if (start) {
        start = dateToSet.hour(start.hour())
      }
      if (end) {
        end = dateToSet.hour(end.hour())
      }

      //handles reqs 2 and 3 by flipping the times if they are out of order
      if (start && end && end.isBefore(start)) {
        const tempHour = start.hour()
        start = start.hour(end.hour())
        end = end.hour(tempHour)
      }

      if ((!start || start.isSame(value[0])) && (!end || end.isSame(value[1]))) return
      onChange?.(...formatOnChangeResult(start, end))
    }, [isSimple, value, onChange])

    const onSimpleDateChange = useCallback(
      (newDate: Dayjs | null) => {
        const [start, end] = formatValueToRange(newDate, value)
        onChange?.(...formatOnChangeResult(start, end))
      },
      [value, onChange]
    )

    const onHourChange = (type: 'start' | 'end', hour?: number) => {
      const [start, end] = cloneDeep(value || [null, null])
      const existingDate = (type === 'start' ? start : end) ?? start ?? end ?? dayjs()
      const result = hour === undefined ? null : setSimpleTime(existingDate.hour(hour))
      const newStart = type === 'start' ? result : start
      const newEnd = type === 'end' ? result : end
      return onChange?.(...formatOnChangeResult(newStart, newEnd))
    }

    const onSimplePanelChange = useCallback<Required<DatePickerProps>['onPanelChange']>(
      (newValue, modes) => {
        const [start, end] = formatValueToRange(newValue, value)
        rest.onPanelChange?.([start, end], [modes, modes])
      },
      [value, rest.onPanelChange]
    )

    const onSimpleOk = useCallback<Required<DatePickerProps>['onOk']>(
      (newValue) => {
        const [start, end] = formatValueToRange(newValue, value)
        rest.onOk?.([start, end])
      },
      [value, rest.onOk]
    )

    return (
      <Horizontal gap={8} alignItems='center'>
        {isSimple ? (
          <>
            <DatePicker
              ref={ref}
              value={value?.[0] ?? value?.[1]}
              onChange={onSimpleDateChange}
              format={formatters.DATE}
              style={simpleInput}
              {...rest}
              disabled={disabled}
              placeholder={placeholder}
              defaultPickerValue={defaultPickerValue}
              disabledTime={disabledTime}
              onCalendarChange={undefined}
              pickerValue={undefined}
              onPickerValueChange={undefined}
              showTime={false}
              mode={mode}
              onPanelChange={rest.onPanelChange ? onSimplePanelChange : undefined}
              onOk={rest.onOk ? onSimpleOk : undefined}
            />
            <Select
              onChange={(v) => onHourChange('start', v)}
              value={value?.[0]?.hour()}
              disabled={disabled}
              options={timeOptions}
              placeholder='Select a start time'
              style={simpleInput}
            />
            <Select
              onChange={(v) => onHourChange('end', v)}
              value={value?.[1]?.hour()}
              disabled={disabled}
              options={timeOptions}
              placeholder='Select an end time'
              style={simpleInput}
            />
          </>
        ) : (
          <DatePicker.RangePicker
            ref={ref}
            value={value}
            onChange={onChange}
            style={{ minWidth: 320 }}
            showTime={{ format: 'HH:mm' }}
            {...rest}
          />
        )}
        <GraviButton appearance='text' icon={<InfoCircleOutlined />} onClick={() => setIsSimple(!isSimple)}>
          Use {isSimple ? 'Custom' : 'Simple'} Window
        </GraviButton>
      </Horizontal>
    )
  }
)
